import React, { useState, useEffect } from 'react';
import { Product, UsageFrequency } from '../../types/place';

interface EditProductForm {
  name: string;
  description: string;
  originalPrice: string;
  discountedPrice: string;
  discountPercentage: string;
  category: string;
  usageFrequency: UsageFrequency;
  isAvailable: boolean;
  hasTimeRestriction: boolean;
  availableFrom: string;
  availableTo: string;
  imageFile?: File;
  imageUrl?: string;
}

interface EditProductModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (form: EditProductForm) => Promise<void>;
  product: Product;
  usageFrequencyOptions: { value: UsageFrequency; label: string }[];
  isUpdating: boolean;
  error: string;
  setError: (error: string) => void;
}

const EditProductModal: React.FC<EditProductModalProps> = ({
  isOpen,
  onClose,
  onSubmit,
  product,
  usageFrequencyOptions,
  isUpdating,
  error,
  setError
}) => {
  const [form, setForm] = useState<EditProductForm>({
    name: '',
    description: '',
    originalPrice: '',
    discountedPrice: '0,00', // Valor padrão válido
    discountPercentage: '0', // Valor padrão válido
    category: '',
    usageFrequency: UsageFrequency.Unlimited,
    isAvailable: true,
    hasTimeRestriction: false,
    availableFrom: '',
    availableTo: '',
  });

  useEffect(() => {
    if (product && isOpen) {
      // Debug: verificar os dados do produto recebidos
      console.log('EditProductModal - Product data received:', product);
      console.log('EditProductModal - Product discountPercentage:', product.discountPercentage);
      console.log('EditProductModal - Product discountPercentage type:', typeof product.discountPercentage);
      
      // Verificar se discountPercentage é válido
      if (typeof product.discountPercentage !== 'number' || isNaN(product.discountPercentage)) {
        console.error('EditProductModal - Invalid discountPercentage in product:', product.discountPercentage);
        setError('Percentual de desconto inválido no produto');
        return;
      }
      
      // Calcular o preço com desconto automaticamente
      const originalPrice = product.originalPrice;
      const discountPercentage = product.discountPercentage;
      const calculatedDiscountedPrice = calculateDiscountedPrice(originalPrice, discountPercentage);
      
      console.log('EditProductModal - Calculated values:', {
        originalPrice,
        discountPercentage,
        calculatedDiscountedPrice
      });
      
      setForm({
        name: product.name,
        description: product.description || '',
        originalPrice: product.originalPrice.toFixed(2).replace('.', ','),
        discountedPrice: calculatedDiscountedPrice.toFixed(2).replace('.', ','),
        discountPercentage: product.discountPercentage.toFixed(0),
        category: product.category,
        usageFrequency: product.usageFrequency || UsageFrequency.Unlimited,
        isAvailable: product.isAvailable,
        hasTimeRestriction: product.hasTimeRestriction,
        availableFrom: formatTime(product.availableFrom),
        availableTo: formatTime(product.availableTo),
        imageUrl: product.imageUrl,
      });
      
      // Debug: verificar o formulário após setForm
      console.log('EditProductModal - Form set with discountPercentage:', product.discountPercentage.toFixed(0));
    }
  }, [product, isOpen]);

  const formatTime = (timeString?: string) => {
    if (!timeString) return '';
    return timeString.substring(0, 5); // Remove segundos se houver
  };

  const handleInputChange = (field: keyof EditProductForm, value: string | boolean) => {
    console.log('EditProductModal - handleInputChange called with field:', field, 'value:', value);
    console.log('EditProductModal - Current form state before update:', form);
    
    // Verificação especial para discountPercentage
    if (field === 'discountPercentage') {
      console.log('EditProductModal - Special handling for discountPercentage field');
      if (typeof value === 'string') {
        const percentage = parseFloat(value);
        if (isNaN(percentage) || percentage < 0 || percentage > 100) {
          console.error('EditProductModal - Invalid discountPercentage value in handleInputChange:', value);
          setError('Percentual de desconto deve ser entre 0 e 100');
          return;
        }
        console.log('EditProductModal - Valid discountPercentage value:', percentage);
      }
    }
    
    // Verificação especial para originalPrice
    if (field === 'originalPrice') {
      console.log('EditProductModal - Special handling for originalPrice field');
      if (typeof value === 'string' && value && form.discountPercentage) {
        const originalPrice = parseFloat(value.replace(',', '.'));
        const discountPercentage = parseFloat(form.discountPercentage);
        
        if (!isNaN(originalPrice) && !isNaN(discountPercentage)) {
          const discountedPrice = calculateDiscountedPrice(originalPrice, discountPercentage);
          console.log('EditProductModal - Auto-calculating discountedPrice:', discountedPrice);
          
          setForm(prev => ({
            ...prev,
            [field]: value,
            discountedPrice: discountedPrice.toFixed(2).replace('.', ',')
          }));
          return;
        }
      }
    }
    
    setForm(prev => {
      const newForm = { ...prev, [field]: value };
      console.log('EditProductModal - New form state after update:', newForm);
      return newForm;
    });
  };

  const calculateDiscountedPrice = (original: number, percentage: number): number => {
    return original * (1 - percentage / 100);
  };

  const handleOriginalPriceChange = (value: string) => {
    console.log('EditProductModal - handleOriginalPriceChange called with value:', value);
    console.log('EditProductModal - Current form discountPercentage:', form.discountPercentage);
    
    // Verificar se o valor é válido
    if (!value || value === '') {
      console.error('EditProductModal - Empty originalPrice value received');
      setForm(prev => ({ ...prev, originalPrice: value }));
      return;
    }
    
    const originalPrice = parseFloat(value.replace(',', '.'));
    const discountPercentage = parseFloat(form.discountPercentage.replace(',', '.'));
    
    console.log('EditProductModal - Parsed values in handleOriginalPriceChange:', { originalPrice, discountPercentage });
    
    // Verificar se os valores parseados são válidos
    if (isNaN(originalPrice) || isNaN(discountPercentage)) {
      console.error('EditProductModal - Invalid parsed values in handleOriginalPriceChange:', { originalPrice, discountPercentage });
      setForm(prev => ({ ...prev, originalPrice: value }));
      return;
    }
    
    if (originalPrice < 0) {
      console.error('EditProductModal - Original price out of range:', originalPrice);
      setForm(prev => ({ ...prev, originalPrice: value }));
      return;
    }
    
    const discountedPrice = calculateDiscountedPrice(originalPrice, discountPercentage);
    console.log('EditProductModal - Calculated discounted price in handleOriginalPriceChange:', discountedPrice);
    
    setForm(prev => ({
      ...prev,
      originalPrice: value,
      discountedPrice: discountedPrice.toFixed(2).replace('.', ',')
    }));
  };

  const handleDiscountPercentageChange = (value: string) => {
    console.log('EditProductModal - handleDiscountPercentageChange called with value:', value);
    console.log('EditProductModal - Current form state:', form);
    
    // Verificar se o valor é válido
    if (!value || value === '') {
      console.error('EditProductModal - Empty discountPercentage value received');
      return;
    }
    
    const percentage = parseFloat(value.replace(',', '.'));
    const originalPrice = parseFloat(form.originalPrice.replace(',', '.'));
    
    console.log('EditProductModal - Parsed values:', { percentage, originalPrice });
    
    // Verificar se os valores parseados são válidos
    if (isNaN(percentage) || isNaN(originalPrice)) {
      console.error('EditProductModal - Invalid parsed values:', { percentage, originalPrice });
      setForm(prev => ({ ...prev, discountPercentage: value }));
      return;
    }
    
    if (percentage < 0 || percentage > 100) {
      console.error('EditProductModal - Percentage out of range:', percentage);
      setForm(prev => ({ ...prev, discountPercentage: value }));
      return;
    }
    
    const discountedPrice = calculateDiscountedPrice(originalPrice, percentage);
    console.log('EditProductModal - Calculated discounted price:', discountedPrice);
    
    setForm(prev => {
      const newForm = {
        ...prev,
        discountPercentage: value,
        discountedPrice: discountedPrice.toFixed(2).replace('.', ',')
      };
      console.log('EditProductModal - New form state after update:', newForm);
      return newForm;
    });
  };

  const handleImageChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      if (!file.type.startsWith('image/')) {
        setError('Por favor, selecione apenas arquivos de imagem');
        return;
      }

      if (file.size > 5 * 1024 * 1024) {
        setError('A imagem deve ter no máximo 5MB');
        return;
      }

      setForm(prev => ({ ...prev, imageFile: file }));
    }
  };

  const removeImage = () => {
    setForm(prev => ({ ...prev, imageFile: undefined, imageUrl: undefined }));
  };

  // const resetForm = () => {
  //   if (product) {
  //     console.log('EditProductModal - resetForm called with product:', product);
  //     console.log('EditProductModal - Product discountPercentage in resetForm:', product.discountPercentage);
      
  //     // Verificar se discountPercentage é válido
  //     if (typeof product.discountPercentage !== 'number' || isNaN(product.discountPercentage)) {
  //       console.error('EditProductModal - Invalid discountPercentage in resetForm:', product.discountPercentage);
  //       setError('Percentual de desconto inválido no produto');
  //       return;
  //     }
      
  //     // Calcular o preço com desconto automaticamente
  //     const originalPrice = product.originalPrice;
  //     const discountPercentage = product.discountPercentage;
  //     const calculatedDiscountedPrice = calculateDiscountedPrice(originalPrice, discountPercentage);
      
  //     console.log('EditProductModal - Calculated values in resetForm:', {
  //       originalPrice,
  //       discountPercentage,
  //       calculatedDiscountedPrice
  //     });
      
  //     const newForm = {
  //       name: product.name,
  //       description: product.description || '',
  //       originalPrice: product.originalPrice.toFixed(2).replace('.', ','),
  //       discountedPrice: calculatedDiscountedPrice.toFixed(2).replace('.', ','), // Sempre calcular
  //       discountPercentage: product.discountPercentage.toFixed(0),
  //       category: product.category,
  //       usageFrequency: product.usageFrequency || UsageFrequency.Unlimited,
  //       isAvailable: product.isAvailable,
  //       hasTimeRestriction: product.hasTimeRestriction,
  //       availableFrom: formatTime(product.availableFrom),
  //       availableTo: formatTime(product.availableTo),
  //       imageUrl: product.imageUrl,
  //     };
      
  //     console.log('EditProductModal - New form in resetForm:', newForm);
  //     console.log('EditProductModal - discountPercentage in new form:', newForm.discountPercentage);
  //     console.log('EditProductModal - discountedPrice in new form:', newForm.discountedPrice);
      
  //     setForm(newForm);
  //   }
  // };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // Debug: verificar os valores antes do envio
    console.log('EditProductModal - Form data before submit:', form);
    console.log('EditProductModal - discountPercentage value:', form.discountPercentage);
    console.log('EditProductModal - discountPercentage type:', typeof form.discountPercentage);
    console.log('EditProductModal - discountedPrice value:', form.discountedPrice);
    console.log('EditProductModal - discountedPrice type:', typeof form.discountedPrice);
    
    // Verificação final antes do envio
    if (!form.discountPercentage || form.discountPercentage === '') {
      console.error('EditProductModal - discountPercentage is empty before submit');
      setError('Percentual de desconto é obrigatório');
      return;
    }
    
    if (!form.discountedPrice || form.discountedPrice === '') {
      console.error('EditProductModal - discountedPrice is empty before submit');
      setError('Preço com desconto é obrigatório');
      return;
    }
    
    const discountPercentage = parseFloat(form.discountPercentage);
    const discountedPrice = parseFloat(form.discountedPrice.replace(',', '.'));
    
    if (isNaN(discountPercentage) || discountPercentage < 0 || discountPercentage > 100) {
      console.error('EditProductModal - Invalid discountPercentage before submit:', form.discountPercentage);
      setError('Percentual de desconto inválido');
      return;
    }
    
    if (isNaN(discountedPrice) || discountedPrice < 0) {
      console.error('EditProductModal - Invalid discountedPrice before submit:', form.discountedPrice);
      setError('Preço com desconto inválido');
      return;
    }
    
    console.log('EditProductModal - Validation passed, submitting form with:', {
      discountPercentage,
      discountedPrice
    });
    
    try {
      await onSubmit(form);
      onClose();
    } catch (err) {
      // Error handling is done in the parent component
    }
  };

  if (!isOpen || !product) {
    return null;
  }

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg p-6 w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        <div className="flex justify-between items-center mb-6">
          <h2 className="text-2xl font-bold text-gray-800">Editar Produto</h2>
          <button
            onClick={onClose}
            className="text-gray-500 hover:text-gray-700 text-2xl"
          >
            ×
          </button>
        </div>

        {error && (
          <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg mb-4">
            {error}
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Nome do Produto *
              </label>
              <input
                type="text"
                value={form.name}
                onChange={(e) => handleInputChange('name', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                required
              />
            </div>

            {/* <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Categoria *
              </label>
              <select
                value={form.category}
                onChange={(e) => handleInputChange('category', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                required
              >
                <option value="">Selecione uma categoria</option>
                {categories.map((category) => (
                  <option key={category} value={category}>
                    {category}
                  </option>
                ))}
              </select>
            </div> */}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Descrição
            </label>
            <textarea
              value={form.description}
              onChange={(e) => handleInputChange('description', e.target.value)}
              rows={3}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Preço Original (R$) *
              </label>
              <input
                type="text"
                value={form.originalPrice}
                onChange={(e) => handleOriginalPriceChange(e.target.value)}
                placeholder="0,00"
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                % de Desconto *
              </label>
              <input
                type="number"
                value={form.discountPercentage}
                onChange={(e) => handleDiscountPercentageChange(e.target.value)}
                min="0"
                max="100"
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Preço com Desconto (R$)
              </label>
              <input
                type="text"
                value={form.discountedPrice}
                readOnly
                className="w-full px-3 py-2 border border-gray-300 rounded-md bg-gray-50"
              />
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Frequência de Uso *
              </label>
              <select
                value={form.usageFrequency}
                onChange={(e) => handleInputChange('usageFrequency', e.target.value as UsageFrequency)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                required
              >
                {usageFrequencyOptions.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Status de Disponibilidade
              </label>
              <div className="flex items-center space-x-4">
                <label className="flex items-center">
                  <input
                    type="radio"
                    name="isAvailable"
                    value="true"
                    checked={form.isAvailable === true}
                    onChange={(e) => handleInputChange('isAvailable', e.target.value === 'true')}
                    className="mr-2"
                  />
                  Disponível
                </label>
                <label className="flex items-center">
                  <input
                    type="radio"
                    name="isAvailable"
                    value="false"
                    checked={form.isAvailable === false}
                    onChange={(e) => handleInputChange('isAvailable', e.target.value === 'true')}
                    className="mr-2"
                  />
                  Indisponível
                </label>
              </div>
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Imagem do Produto
            </label>
            {form.imageUrl && (
              <div className="mb-2">
                <img 
                  src={form.imageUrl} 
                  alt="Imagem atual" 
                  className="w-20 h-20 object-cover rounded-lg"
                />
              </div>
            )}
            <input
              type="file"
              accept="image/*"
              onChange={handleImageChange}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
            {(form.imageFile || form.imageUrl) && (
              <div className="mt-2 flex items-center space-x-2">
                <span className="text-sm text-gray-600">
                  {form.imageFile ? form.imageFile.name : 'Imagem atual'}
                </span>
                <button
                  type="button"
                  onClick={removeImage}
                  className="text-red-600 hover:text-red-800 text-sm"
                >
                  Remover
                </button>
              </div>
            )}
          </div>

          {/* <div className="flex items-center space-x-2">
            <input
              type="checkbox"
              id="hasTimeRestriction"
              checked={form.hasTimeRestriction}
              onChange={(e) => handleInputChange('hasTimeRestriction', e.target.checked)}
              className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
            />
            <label htmlFor="hasTimeRestriction" className="text-sm font-medium text-gray-700">
              Restrição de Horário
            </label>
          </div> */}

          {form.hasTimeRestriction && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Disponível a partir de
                </label>
                <input
                  type="time"
                  value={form.availableFrom}
                  onChange={(e) => handleInputChange('availableFrom', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Disponível até
                </label>
                <input
                  type="time"
                  value={form.availableTo}
                  onChange={(e) => handleInputChange('availableTo', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
            </div>
          )}

          <div className="flex justify-end space-x-3 pt-4">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-gray-700 bg-gray-200 rounded-md hover:bg-gray-300 focus:outline-none focus:ring-2 focus:ring-gray-500"
            >
              Cancelar
            </button>
            <button
              type="submit"
              disabled={isUpdating}
              className="btn btn-primary"
            >
              {isUpdating ? 'Atualizando...' : 'Atualizar Produto'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default EditProductModal;
